import { useEffect, useMemo, useRef, useState } from "react";
import { Card } from "primereact/card";
import { BreadCrumb } from 'primereact/breadcrumb';
import type { MenuItem } from "primereact/menuitem";
import { Calendar } from 'primereact/calendar';
import Table from "../components/table";
import { IconCalendar, IconDownload, IconEye, IconFileTypeCsv, IconFileTypeXls } from '@tabler/icons-react';
import { TABLE_COLUMNS_CONFIG, type ColumnMeta } from "../constants/table.cnst";
import type { EVENT_TABLE, EventsApiResponse } from "../interfaces/dashboard";
import { Tag } from "primereact/tag";
import { getSeverity, formatDateInDDMMYYYY, ddmmyyyyToMmddyyyy } from "../services/util.service";
import { Button } from "primereact/button";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { fetchApi } from "../services/http.service";
import { APIMapping } from "../constants/api-mapping.cnst";
import { STATION_EVENTS_FILTERS, HBL_PA_RESOURCES, RCA_BACK_BUTTON, EVENT_CATEGORIES } from "../constants/data.cnst";
import { Menu } from "primereact/menu";
import { Toast } from "primereact/toast";
import BarChart from "../components/bar-charts";


function StationEvents() {
  const eventTableColumns: ColumnMeta[] = TABLE_COLUMNS_CONFIG.STATION_EVENTS;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const fromDateParam = searchParams.get("fromDate");
  const toDateParam = searchParams.get("toDate");

  useEffect(() => {
    if (searchParams.get("callApi")) {
      searchParams.delete('callApi');
      setSearchParams(searchParams, { replace: true });
      handleDateChange([pastMonth, today], 'fromNavigation');
    }
  }, [fromDateParam, toDateParam]);

  const fromDate = searchParams.get("fromDate");
  const toDate = searchParams.get("toDate");

  const { today, pastMonth } = useMemo(() => {
    const t = new Date();
    const p = new Date();
    p.setMonth(t.getMonth() - 1);
    return { today: t, pastMonth: p };
  }, []);


  const [dates, setDates] = useState<(Date | null)[]>([
    fromDate ? new Date(ddmmyyyyToMmddyyyy(fromDate)) : pastMonth,
    toDate ? new Date(ddmmyyyyToMmddyyyy(toDate)) : today,
  ]);


  // const saveFiltersToStorage = (filters: any) => {
  //   localStorage.setItem(STATION_EVENTS_FILTERS, JSON.stringify(filters));
  // };

  // const getFiltersFromStorage = () => {
  //   try {
  //     return JSON.parse(localStorage.getItem(STATION_EVENTS_FILTERS) || "{}");
  //   } catch {
  //     return {};
  //   }
  // };

  const clearFiltersFromStorage = () => {
    localStorage.removeItem(STATION_EVENTS_FILTERS);
  };

  // const saved = getFiltersFromStorage();

  const pageFromUrl = Number(searchParams.get("page") || 1);
  const rowsFromUrl = Number(searchParams.get("rows") || 10);

  const [rows, setRows] = useState(rowsFromUrl);
  const [first, setFirst] = useState((pageFromUrl - 1) * rowsFromUrl);
  // const [selectedStations, setSelectedStations] = useState(saved?.stations ?? []);
  // const [selectedZone, setSelectedZone] = useState<string>();
  // const [selectedDivision, setSelectedDivision] = useState<string>();
  // const [selectedEvent, setSelectedEvent] = useState<string[]>(saved?.eventCategories ?? []);




  const eventsStats = [
    { label: "No. of Events", value: '-', color: "#53D28C", key: "totalEvents", abortedTrips: 0, completedTrips: 0, openTrips: 0, },
    { label: "No. of Undesirable Brakings", value: '-', color: "#FFB269", key: "totalUnwantedBrakings" },
    { label: "No. of Mode Degradations", value: '-', color: "#EA5455", key: "totalModeDegradations" },
    { label: "No. of SOC MA Fluctuations", value: '-', color: "#548eea", key: "totalSocMaFluctuations" }
  ]


  //trips count api call
  // const [tripsSummaryQueryParams, setTripsSummaryQueryParams] = useState({
  //   fromDate: formatDateInDDMMYYYY(dates[0]),
  //   toDate: formatDateInDDMMYYYY(dates[1])
  // });

  // const { data: tripCountsData, isFetching: isTripDataLoading } = useQuery({
  //   queryKey: ["station-kpi-count", tripsSummaryQueryParams],
  //   queryFn: () =>
  //     fetchApi({
  //       endpoint: APIMapping.KPI_COUNT,
  //       queryParams: tripsSummaryQueryParams,
  //       resourceName: HBL_PA_RESOURCES.STATION_EVENTS
  //     }),
  //   enabled: !!tripsSummaryQueryParams
  // });

  // const eventsStatistics = useMemo(() => {
  //   if (!tripCountsData?.result) return eventsStats;

  //   return eventsStats.map(stat => ({
  //     ...stat,
  //     value: tripCountsData?.response?.[stat.key] ?? '-',
  //     // abortedTrips: tripCountsData?.response?.abortedTrips ?? '-',
  //     // completedTrips: tripCountsData?.response?.completedTrips ?? '-',
  //     // openTrips: tripCountsData?.response?.openTrips ?? '-',
  //   }));
  // }, [tripCountsData]);

  // events api call
  const [eventsQueryParams, setEventsQueryParams] = useState({
    fromDate: formatDateInDDMMYYYY(dates[0]),
    toDate: formatDateInDDMMYYYY(dates[1]),
    total_entry_per_page: rowsFromUrl,
    page_num: pageFromUrl,
    // locoIds: selectedStations ?? null,
    // eventCategories: selectedEvent?.length
    //   ? selectedEvent?.map((t: string) =>
    //     t.replaceAll(" ", "_").replaceAll("/", "_").toUpperCase()
    //   )
    //   : null,
  });


  const { data: eventsApiResponse, isFetching } = useQuery<EventsApiResponse>({
    queryKey: ["events", eventsQueryParams],
    queryFn: () =>
      fetchApi({
        endpoint: APIMapping.STATION_EVENTS,
        queryParams: eventsQueryParams,
        resourceName: HBL_PA_RESOURCES.STATION_EVENTS
      }),
    enabled: Boolean(eventsQueryParams.fromDate && eventsQueryParams.toDate)
  });

  const eventTableData: EVENT_TABLE[] =
    eventsApiResponse?.response?.data?.map((rowData: EVENT_TABLE) => {
      return {
        ...rowData,
        eventCategory: rowData.eventCategory.replace('_', ' '),
        eventId: rowData.eventId,
        absoluteLocation: rowData.absoluteLocation || 'NA',
        displayEventId: rowData.eventId.substring(0, 5) + '...'
      };
    }) ?? [];

  const totalEvents = eventsApiResponse?.response?.total_entries ?? 0;

  // handle data change and set query params
  const handleDateChange = (value: (Date | null)[], type: string) => {
    setDates(value);
    clearFiltersFromStorage();
    // setSelectedEvent([]);
    // setSelectedStations([])

    if (!value?.[0]) return;

    const payload = {
      fromDate: formatDateInDDMMYYYY(value[0]),
      toDate: formatDateInDDMMYYYY(value[1]) || formatDateInDDMMYYYY(value[0]),
    };
    setFirst(0);
    setRows(10);
    setEventsQueryParams((prev: any) => ({ ...prev, ...payload, page_num: 1, total_entry_per_page: 10, eventCategories: null, locoIds: null }));
    // setTripsSummaryQueryParams((prev: any) => ({ ...prev, ...payload }));
    // setSelectedZone("All");
    if (type === 'fromCalendar') {
      setSearchParams({ fromDate: payload.fromDate, toDate: payload.toDate }, { replace: true });
    }
  };

  const breadcrumb: MenuItem[] = [
    { label: 'Station Events', style: { pointerEvents: 'none' }, className: 'font-semibold text-gray-900' }
  ];

  // const { data: filtersResponseData } = useQuery({
  //   queryKey: ["station-events-filters", tripsSummaryQueryParams],
  //   queryFn: () =>
  //     fetchApi({
  //       endpoint: APIMapping.GET_FILTERS_FOR_EVENTS,
  //       queryParams: { ...tripsSummaryQueryParams, filterTypes: 'STATION' },
  //       resourceName: HBL_PA_RESOURCES.STATION_EVENTS
  //     }),
  //   enabled: !!tripsSummaryQueryParams
  // });
    const { data: eventsCategoryResponseData } = useQuery({
    queryKey: ["events-category", eventsQueryParams],
    queryFn: () =>
      fetchApi({
        endpoint: APIMapping.EVENT_SUMMARY,
        queryParams: eventsQueryParams,
        resourceName: HBL_PA_RESOURCES.EVENTS
      }),
    enabled: Boolean(eventsQueryParams.fromDate && eventsQueryParams.toDate)
  });

  const getLocoBarChartData = (data: any, key: string) => {
      return {
        labels: data.map((item: any) => String(item[key])),
  
        datasets: EVENT_CATEGORIES.map((event) => ({
          label: event.label,
          backgroundColor: event.backgroundColor,
          data: data.map(
            (item: any) =>
              item?.breakdownByCategory?.[event.key] ?? 0
          )
        }))
      };
    };
    
  const stationBarChartData = getLocoBarChartData(eventsCategoryResponseData?.response?.topStationsByEvents ?? [], 'stationId');
  const toast = useRef<Toast>(null);

  const handleEventView = (rowData: any) => {
    const params = new URLSearchParams(location.search);
    params.set("eventId", rowData.eventId);
    localStorage.setItem(RCA_BACK_BUTTON, location.pathname + location.search);

    navigate(`/dashboard/station-events-rca?${params.toString()}`);
  };

  const { mutateAsync: downloadEvents, isPending: isDownloading } = useMutation({
    mutationFn: (format: "CSV" | "EXCEL") =>
      fetchApi({
        endpoint: APIMapping.REPORTS_EXPORT,
        responseType: "blob",
        queryParams: { ...eventsQueryParams, format, reportType: "STATION_EVENTS", },
        resourceName: HBL_PA_RESOURCES.STATION_EVENTS,
      }),
  });

  const handleDownload = async (format: "CSV" | "EXCEL") => {
    try {
      const result = await downloadEvents(format);
      const blob = result.data as Blob;

      const contentDisposition = result.headers.get("content-disposition");
      let fileName = 'REPORT_TYPE_EVENTS'; // fallback

      if (contentDisposition) {
        console.log(contentDisposition)
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match?.[1]) {
          fileName = match[1];
        }
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");

      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();

      a.remove();
      window.URL.revokeObjectURL(url);

      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: `Station Events downloaded successfully`,
        life: 3000
      });
    } catch (err) {
      console.error("Download failed", err);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Unable to download station events. Please try again.",
        life: 4000
      });
    }
  };

  const items = [
    {
      label: "CSV", icon: <IconFileTypeCsv />,
      command: () => { handleDownload("CSV"); }
    },
    {
      label: "Excel", icon: <IconFileTypeXls />,
      command: () => { handleDownload("EXCEL"); }
    }
  ];
  const menu = useRef<Menu>(null);


  const eventsTableHeader = (

    <div className="flex justify-between items-center">
      <h3 className="m-0 font-semibold">Station Events</h3>

      <Button
        label="Download"
        icon={<IconDownload size={20} />}
        loading={isDownloading}
        disabled={isDownloading || eventTableData.length === 0}
        className="bg-brand-600 border-brand-600 text-white dark:bg-brand-400 dark:border-brand-500 "
        onClick={(event) => menu.current?.toggle(event)}
      />
      <Menu popup ref={menu} model={items} id="popup_menu" popupAlignment="right" />

      {/* {selectedEventInEventsDistributionChart && (
        <Button
          className="bg-gray-200 border-gray-200 text-gray-700 dark:bg-gray-700 dark:border-gray-700 dark:text-gray-200"
          onClick={() => {
            setSelectedEventInEventsDistributionChart('');
            setFirst(0);
            setRows(10);

            setEventsQueryParams((prev: any) => {
              const { eventCategories, ...rest } = prev;
              return { ...rest, page_num: 1, total_entry_per_page: 10 };
            });
          }}
        >
          <span>{selectedEventInEventsDistributionChart}</span>
          <IconX size={20} />
        </Button>
      )} */}
    </div>

  );

  return (
    <div className="h-[calc(100vh-4rem)] p-6 flex-1 overflow-y-auto">
      <Toast ref={toast} position="top-right" />

      <BreadCrumb model={breadcrumb} />

      <div className="flex items-center justify-between w-full my-3">
        {/* <h2 className="text-xl font-medium">Events</h2> */}

        <div className="flex flex-col items-start relative ml-auto">
          <Calendar
            id="date"
            value={dates}
            onChange={(e) => { handleDateChange(e.value as (Date | null)[], 'fromCalendar'); }}
            selectionMode="range"
            readOnlyInput
            hideOnRangeSelection
            dateFormat="dd/mm/yy"
            numberOfMonths={2}
            placeholder="Select Date"
            className="w-full min-w-64"
            inputClassName="w-64 pr-10"
          />
          <IconCalendar size={18} stroke={1.5} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none z-10" />
        </div>
      </div>


      {/* Top Stats */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-6">

        {/* ---------- ROW 1 : 3 STAT CARDS ---------- */}
        {eventsStats.map((event, index) => (
          <div className="md:col-span-3" key={event.label}>
            <Card className="h-full pointer-events-none opacity-60" key={index}>
              {/* {index === 0 && !isTripDataLoading && (
                <Tooltip target=".trip-summary-tooltip" position="top">
                  <div className="text-sm">
                    <div>Aborted Trips: <b>{event.abortedTrips}</b></div>
                    <div>Completed Trips: <b>{event.completedTrips}</b></div>
                    <div>Open Trips: <b>{event.openTrips}</b></div>
                  </div>
                </Tooltip>
              )} */}
              <div className={`pl-5 relative ${index === 0 ? "trip-summary-tooltip cursor-pointer" : ""
                }`}>
                <span
                  className="absolute left-0 top-0 w-[.25rem] rounded-full h-full"
                  style={{ backgroundColor: event.color }}
                ></span>
                {/* {isTripDataLoading ? (
                  <>
                    <Skeleton width="60%" height="1.5rem" className="mb-2" />
                    <Skeleton width="40%" height="1.5rem" />
                  </>
                ) : ( */}
                <>
                  <p className="text-gray-700 dark:text-gray-100">{event.label}</p>
                  <h2 className="text-3xl text-gray-900 dark:text-gray-100 font-bold">
                    {event.value}
                  </h2>
                </>
                {/* )} */}
              </div>
            </Card>
          </div>
        ))}

        <div className="md:col-span-12">
          <Card header="Most Problematic Stations">
            <BarChart id='stations' data={stationBarChartData} className="h-80" yText="No. Of Events" xText="Station ID" stacked={true} barThickness={true} />
          </Card>
        </div>

        {/* <div className="md:col-span-4">
          <Card className="h-full" header="Events Distribution">
            <DonutChart
              data={eventsDistributionData}
              id="events-distribution"
              selectedEventInEventsDistributionChart={selectedEventInEventsDistributionChart}
            />
          </Card>
        </div>

        <div className="md:col-span-4">
          <Card className="h-full" header={eventsOverTimeHeader}>

            <LineChart id="events-over-time" data={eventsOverTimeData} xOffset={true} isScroll />
          </Card>
        </div>

        <div className="md:col-span-4">
          <Card className="h-full p-card-body-p-0" header="Events Distribution by Region">
            <IndiaRailwayMap className="h-74" eventPoints={eventPoints} selectedZone={selectedZone} onZoneChange={setSelectedZone} />
          </Card>
        </div>

        <div className="md:col-span-6">

          <Card header="Most Problematic Locos">
            <BarChart id='locos' data={locoBarChartData} className="h-80" yText="No. Of Events" xText="Loco ID" stacked={true} barThickness={true} />
          </Card>
        </div> */}


      </div>


      {/* ---------- ROW 4 : 1 CARD ---------- */}
      <div className="grid grid-cols-1 mb-6">
        <Card header={eventsTableHeader} className="p-card-body-p-0">

          {/* <div className="flex gap-2 p-2 justify-end">

        <MultiSelect
        disabled
          value={selectedZone}
          options={RAILWAY_ZONES} // [{ label, value }]
          optionLabel="zoneName"
          optionValue="zoneCode"
          filter
          onChange={(e: any) => {
            const selected = e.value;
            setSelectedZone(selected);

            const params = new URLSearchParams(searchParams);
            params.set("page", String(1));
            params.set("rows", String(10));
            setSearchParams(params, { replace: true });

            setFirst(0);
            setRows(10);

            setEventsQueryParams((prev: any) => ({
              ...prev,
              zones: selected.join(","),
              page_num: 1,
              total_entry_per_page: 10,
            }));

            saveFiltersToStorage({
              zones: selected,
              divisions: selectedDivision,
              stations: selectedStations,
              eventCategories: selectedEvent,
            });
          }}
          placeholder="Select Zones"
          className="w-66"
          display="chip"
        />

        <MultiSelect
        disabled
          value={selectedDivision}
          options={filtersResponseData?.response?.locoIds ?? []} // [{ label, value }]
          filter
          onChange={(e: any) => {
            const selected = e.value;
            setSelectedDivision(selected);

            const params = new URLSearchParams(searchParams);
            params.set("page", String(1));
            params.set("rows", String(10));
            setSearchParams(params, { replace: true });

            setFirst(0);
            setRows(10);

            setEventsQueryParams((prev: any) => ({
              ...prev,
              divisions: selected.join(","),
              page_num: 1,
              total_entry_per_page: 10,
            }));

            saveFiltersToStorage({
              zones: selectedZone,
              divisions: selected,
              stations: selectedStations,
              eventCategories: selectedEvent,
            });
          }}
          placeholder="Select Divisions"
          className="w-56"
          display="chip"
        />

        <MultiSelect
        disabled
          value={selectedStations}
          options={filtersResponseData?.response?.stationIds ?? []} // [{ label, value }]
          filter
          onChange={(e: any) => {
            const selected = e.value;
            setSelectedStations(selected);

            const params = new URLSearchParams(searchParams);
            params.set("page", String(1));
            params.set("rows", String(10));
            setSearchParams(params, { replace: true });

            setFirst(0);
            setRows(10);

            setEventsQueryParams((prev: any) => ({
              ...prev,
              stations: selected.join(","),
              page_num: 1,
              total_entry_per_page: 10,
            }));

            saveFiltersToStorage({
              zones: selectedZone,
              divisions: selectedDivision,
              stations: selected,
              eventCategories: selectedEvent,
            });
          }}
          placeholder="Select Stations"
          className="w-62"
          display="chip"
        />

        <MultiSelect
        disabled
          value={selectedEvent}
          options={STATION_EVENTS_CATEGORIES.map(event => event.label)} // [{ label, value }]
          filter
          onChange={(e) => {
            const selected = e.value;

            const params = new URLSearchParams(searchParams);
            params.set("page", String(1));
            params.set("rows", String(10));
            setSearchParams(params, { replace: true });

            setSelectedEvent(selected);
            setFirst(0);
            setRows(10);

            setEventsQueryParams((prev: any) => ({
              ...prev,
              eventCategories: selected?.map((t: string) =>
                t.replaceAll(" ", "_").replaceAll("/", "_").toUpperCase()
              ).join(","),
              page_num: 1,
              total_entry_per_page: 10,
            }));
            saveFiltersToStorage({
              zones: selectedZone,
              divisions: selectedDivision,
              stations: selectedStations,
              eventCategories: selected,
            });
          }}
          placeholder="Select Events"
          className="w-66"
          display="chip"
        />
      </div> */}
          <Table
            columns={eventTableColumns}
            data={eventTableData}
            showGridlines={true}
            paginator={true}
            isLoading={isFetching}
            first={first}
            rows={rows}
            totalRecords={totalEvents}
            renderCell={(rowData, field) => {
              if (field === 'eventCategory') {
                return <Tag value={rowData[field]} severity={getSeverity(rowData[field])}></Tag>
              }
              if (field === 'action') {
                return <Button className="bg-gray-200 border-gray-200 text-gray-700 dark:bg-gray-700 dark:border-gray-700 dark:text-gray-200" onClick={() => handleEventView(rowData)} icon={<IconEye size={20} />} label="View" outlined />
              }
            }}
            onPageChangeExternal={(page_num, total_entry_per_page) => {
              setFirst((page_num - 1) * total_entry_per_page);
              setRows(total_entry_per_page);
              setEventsQueryParams((prev: any) => ({
                ...prev,
                page_num: page_num,
                total_entry_per_page: total_entry_per_page
              }));


              const params = new URLSearchParams(searchParams);
              params.set("page", String(page_num));
              params.set("rows", String(total_entry_per_page));
              setSearchParams(params, { replace: true });
            }}
          />
        </Card>
      </div>

    </div>
  );
}

export default StationEvents;
